import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Card, Button, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { planTier } from "../utils/planFeatures";
import { displayPlanName as sharedDisplayPlanName } from "../utils/planNames";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import StripeCreatePayMethod from "../components/StripeCreatePayMethod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleLeft, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import {
  resetCreatePaymentIntent,
  resetConfirmSubscription,
  fetchRestaurantDetails,
  confirmSubscriptionCheckoutSessionAction,
  syncSubscriptionFromStripeAction,
  cancelScheduledDowngradeAction,
} from "../actions/restaurantActions";
import LoaderComponent from "../components/LoaderComponent";
import styled, { css } from "styled-components";
import {
  Container,
  CardHeader,
  CardHeaderTitle,
} from "../components/DefaultImports";
import { ConfirmationModal } from "../components/ConfirmationModal";
import {
  consumeRestaurantSetupTourPending,
  startRestaurantSetupTour,
} from "../utils/restaurantSetupTour";
import {
  createUpgradeIntent,
  activateFreePlan,
  activateStarterPack,
  fetchSubscriptionStatus,
} from "../actions/paymentActions";

/* --------- CONFIG ---------- */
const FEATURE_PREVIEW_COUNT = 7;

/* ---------- Paleta de cores cíclica ---------- */
const palette = ["#9FBBC1", "#EE754C", "#99B0AD"];
const getPlanColor = (subscription_number) =>
  palette[(Number(subscription_number) - 1) % palette.length];

/* ---------- Styled-components ---------- */
const ButtonPlans = styled.div`
  padding: 10px 24px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  background-color: ${(props) => (props.isSelected ? "#ee754c" : "#ffffff")};
  color: ${(props) => (props.isSelected ? "#ffffff" : "#ee754c")};
  border: 2px solid #ee754c;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  margin: 5px;
  box-shadow: ${(props) =>
    props.isSelected ? "0 6px 12px rgba(238,117,76,0.2)" : "none"};

  &:hover {
    background-color: #ee561b;
    color: #ffffff;
  }
`;
const PlanCard = styled(Card)`
  border-radius: 18px;
  border: 1px solid #dee2e6;
  background-color: #ffffff;
  padding: 20px 16px;
  text-align: center;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
  min-height: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-3px);
  }

  ${(p) =>
    p.$selected &&
    css`
      border-color: #ee754c;
      box-shadow: 0 6px 16px rgba(238, 117, 76, 0.18);
    `}

  ${(p) =>
    p.$disabledPlan &&
    css`
      opacity: 0.82;
      background: #fffdf8;
    `}
`;
const ActiveBadge = styled.div`
  display: inline-block;
  background: #28a745;
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  margin-bottom: 8px;
`;
const ScheduledBadge = styled(ActiveBadge)`
  background: #856404;
`;
const PaymentPlanCard = styled(PlanCard)`
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 28px;
`;
const PlanName = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: ${({ color }) => `${color || "#333"} !important`};
  margin-bottom: 8px;
`;
const PlanDescription = styled.p`
  font-size: 13px;
  color: #444;
  margin-bottom: 16px;
  min-height: 40px;
`;
const PlanPrice = styled.div`
  font-size: 26px;
  font-weight: bold;
  color: ${({ color }) => color || "#333"};
`;
const PerMonth = styled.div`
  font-size: 13px;
  color: #99b0ad;
  margin-bottom: 12px;
`;
const SubscribeButton = styled(Button)`
  background-color: ${({ color }) => color || "#ee754c"};
  border: none;
  border-radius: 999px;
  padding: 9px 22px;
  font-weight: 700;
  font-size: 14px;
  margin: 12px auto 16px;

  &:hover {
    background-color: #ee561b;
  }

  &:disabled {
    background-color: #c8c8c8;
    border-color: #c8c8c8;
    cursor: not-allowed;
    opacity: 0.85;
  }
`;
const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  text-align: left;
  margin-top: 10px;
  margin-bottom: 0;

  li {
    display: flex;
    align-items: flex-start;
    font-size: 13px;
    color: #333;
    margin-bottom: 7px;
    font-family: "Kanit", sans-serif;

    svg {
      color: ${({ iconColor }) => iconColor || "#ee754c"};
      margin-right: 8px;
      font-size: 12px;
      margin-top: 4px;
      flex-shrink: 0;
    }
  }
`;
const PriceLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
`;
const DiscountNote = styled.div`
  font-size: 12px;
  color: #28a745;
  font-style: italic;
  margin-top: -6px;
  margin-bottom: 10px;
`;
const PaymentTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 16px;
  color: var(--app-orange, #ee754c);
  text-align: center;
`;
const ViewMore = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.color || "#ee754c"};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-top: 6px;

  &:hover {
    text-decoration: underline;
  }
`;
const BackButton = styled(Button)`
  float: right;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  background-color: transparent;
  border: 1px solid #ee754c;
  color: #ee754c;
  transition: all 0.3s ease;

  svg {
    margin-right: 6px;
  }

  &:hover {
    background-color: #e5611b;
    color: #ffffff;
    border-color: #e5611b;
  }
`;
const WarningInline = styled.div`
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.35;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 8px 10px;
  display: inline-block;
  text-align: left;
`;
const ScheduledDowngradeNotice = styled.div`
  width: 100%;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.45;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 14px;
  padding: 12px 14px;
  text-align: center;

  b {
    color: #6f5100;
  }
`;
const OfferItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 8px;
  margin-bottom: 8px;
  color: #333;
  font-size: 13px;

  .icon {
    margin-top: 2px;
    color: ${({ iconColor }) => iconColor || "#ee754c"};
  }
  .text {
    line-height: 1.2;
  }
  .title {
    font-weight: 700;
    margin: 0;
  }
  .desc {
    margin: 1px 0 0;
    color: #4a4a4a;
    font-size: 12px;
  }
`;
const StarterPackButton = styled.button`
  width: 100%;
  max-width: 920px;
  border: none;
  border-radius: 18px;
  padding: 16px 20px;
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(135deg, #ee754c 0%, #d9531e 100%);
  box-shadow: 0 12px 30px rgba(238, 117, 76, 0.28);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  text-align: center;
  position: sticky;
  top: 12px;
  z-index: 9;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 34px rgba(238, 117, 76, 0.34);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 14px 16px;
  }
`;
const StarterPackSubtext = styled.div`
  font-size: 13px;
  font-weight: 500;
  margin-top: 4px;
  opacity: 0.96;
`;
const StarterPackInfoBox = styled.div`
  width: 100%;
  background: #fff8f4;
  border: 1px solid #ffd3c1;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 6px 18px rgba(238, 117, 76, 0.12);
`;
const StarterPackInfoTitle = styled.h4`
  font-size: 22px;
  font-weight: 800;
  color: #ee754c;
  margin-bottom: 12px;
  text-align: center;
`;
const StarterPackInfoText = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #444;
  margin-bottom: 18px;
  text-align: center;
`;
const StarterPackSubscribeButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 14px 18px;
  font-size: 16px;
  font-weight: 800;
  color: #ffffff;
  background: #ee754c;
  box-shadow: 0 8px 20px rgba(238, 117, 76, 0.22);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    background: #e5611b;
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(238, 117, 76, 0.28);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
const SeeMorePlansButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: auto;
  padding-top: 10px;
  align-self: center;

  &:hover {
    color: #ee754c;
    text-decoration: underline;
  }
`;
const ComparisonSection = styled.section`
  margin-top: 10px;
  padding-top: 10px;
`;
const ComparisonHeader = styled.div`
  text-align: center;
  margin-bottom: 18px;
`;
const ComparisonTitle = styled.h3`
  font-size: 24px;
  font-weight: 800;
  color: var(--app-orange, #ee754c);
  margin-bottom: 6px;
`;
const ComparisonSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;
const ComparisonBlock = styled.div`
  margin-bottom: 24px;
`;
const ComparisonBlockTitle = styled.h4`
  font-size: 18px;
  font-weight: 800;
  color: #ee754c;
  margin-bottom: 10px;
`;
const ComparisonTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #ececec;
  border-radius: 16px;
  background: #fff;
`;
const ComparisonTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
    text-align: center;
    font-size: 14px;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    min-width: 240px;
    background: #fcfcfc;
  }

  thead th {
    background: #faf6e9;
    font-weight: 800;
    color: #333;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;
const TableFeatureTitle = styled.div`
  font-weight: 700;
  color: #333;
`;
const TableFeatureDesc = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;
const YesMark = styled.span`
  color: #28a745;
  font-weight: 800;
  font-size: 18px;
`;
const NoMark = styled.span`
  color: #c8c8c8;
  font-weight: 700;
  font-size: 18px;
`;
const PriceCell = styled.div`
  font-weight: 800;
  color: #333;
`;
const SecondaryPrice = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 4px;
`;

/* ---------- Helpers ---------- */
const splitOffersSafely = (offersString) => {
  if (!offersString) return [];
  return offersString
    .split(/\s*;\s*/g)
    .map((s) => s.trim())
    .filter(Boolean);
};
const splitTitleAndDesc = (item) => {
  const idx = item.indexOf(":");
  if (idx === -1) {
    return {
      title: item.trim(),
      desc: "",
    };
  }
  return {
    title: item.slice(0, idx).trim(),
    desc: item.slice(idx + 1).trim(),
  };
};
const normalizeFeatureKey = (value) => value.trim().toLowerCase();
// The 4/5/6 -> 1/2/3 collapse lives in utils/planFeatures now, so the billing
// screens and the feature gates cannot drift apart.
const normalizeSubLevel = planTier;

/*
 * Which plan a click should actually select.
 *
 * While the free-month offer is available, the premium plan costs nothing,
 * needs no card, and drops to Free on its own when the month ends. A
 * restaurant choosing Free is therefore strictly better off on the trial:
 * same price, same destination, a month of the full product on the way. So
 * every selection routes to premium while the offer stands.
 *
 * The exception is an existing subscriber choosing Free, who is asking to
 * downgrade. Routing them to premium would leave them unable to leave their
 * paid plan.
 */
export const shouldUseTrialPlanInstead = ({
  selectedNumber,
  hasStarterPack3Months,
  hasPremiumMonthly,
  activeOrTrial,
}) => {
  if (!hasStarterPack3Months || !hasPremiumMonthly) return false;

  const isFreeSelection = selectedNumber === 1;
  const isDowngradeToFree = isFreeSelection && Boolean(activeOrTrial);

  return !isDowngradeToFree;
};

/* Tier 1 is sold as a free plan with a monthly reservation cap, so it is named
   for what it is rather than by the package row's "Essencial". Kept in step
   with the same helper in ActiveSubscription. Covers package 1 and its yearly
   twin 4. */
const displayPlanName = sharedDisplayPlanName;
const formatMoney = (value, suffix = "") => {
  const num = Number(value ?? 0);
  if (num === 0) return "Grátis";
  return `${value} €${suffix}`;
};
const buildComparisonRows = (subscriptions) => {
  const sortedSubs = [...subscriptions].sort(
    (a, b) => a.subscription_number - b.subscription_number,
  );
  const featureMap = new Map();
  sortedSubs.forEach((sub, index) => {
    splitOffersSafely(sub.offers || "").forEach((raw) => {
      const { title, desc } = splitTitleAndDesc(raw);
      const key = normalizeFeatureKey(title);
      if (!featureMap.has(key)) {
        featureMap.set(key, {
          key,
          title,
          desc,
          introducedAtIndex: index,
        });
      }
    });
  });
  return Array.from(featureMap.values());
};
function ChoosePlanAndPay({ showHeader = true }) {
  const { t, i18n } = useTranslation();
  const appLocale = (i18n.resolvedLanguage || i18n.language || "en").startsWith(
    "pt",
  )
    ? "pt-PT"
    : (i18n.resolvedLanguage || i18n.language || "en").startsWith("es")
      ? "es-ES"
      : "en-GB";
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const comparisonRef = useRef(null);
  const checkoutHandledRef = useRef(false);

  /* Redux selectors */
  const { getSubscriptionPackages } = useSelector(
    (state) => state.subscriptionPackagesStore,
  );
  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails || {};
  const restaurant_sub = useSelector((state) => state.restaurant_subscription);
  const { restaurantSubscription } = restaurant_sub;
  const invoicesState = useSelector((s) => s.payments);
  const { upgradePaymentIntent } = invoicesState;

  /* Estado local */
  const [activeOrTrial, setActiveOrTrial] = useState(false);
  const [activeSubNumber, setActiveSubNumber] = useState(0);
  const [downgradeAtPeriodEnd, setDowngradeAtPeriodEnd] = useState(0);
  const [cadence, setCadence] = useState(null);
  const [subEndIso, setSubEndIso] = useState(null);
  const [monthlySubscriptions, setMonthlySubscriptions] = useState([]);
  const [yearlySubscriptions, setYearlySubscriptions] = useState([]);
  const [showMonthly, setShowMonthly] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [step, setStep] = useState("choose_subscription");
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [_isConfirmationModal, _setIsConfirmationModal] = useState(false);
  const [isActivatingStarterPack, setIsActivatingStarterPack] = useState(false);
  const [isActivatingFreePlan, setIsActivatingFreePlan] = useState(false);
  const [isFinalizingCheckout, setIsFinalizingCheckout] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [hostedReturnType, setHostedReturnType] = useState(null);
  const [isCancellingScheduledDowngrade, setIsCancellingScheduledDowngrade] =
    useState(false);
  const currentRestaurantSub = Array.isArray(restaurantSubscription)
    ? restaurantSubscription[0]
    : restaurantSubscription;
  const availableOffers = Array.isArray(currentRestaurantSub?.available_offers)
    ? currentRestaurantSub.available_offers
    : [];
  const hasStarterPack3Months = availableOffers.includes(
    "starter_pack_3_months",
  );
  const visibleSubscriptions = showMonthly
    ? monthlySubscriptions
    : yearlySubscriptions;
  const premiumMonthlySubscription =
    monthlySubscriptions.length > 0
      ? monthlySubscriptions[monthlySubscriptions.length - 1]
      : null;
  const comparisonPlans = useMemo(() => {
    if (monthlySubscriptions.length > 0) {
      return [...monthlySubscriptions].sort(
        (a, b) => a.subscription_number - b.subscription_number,
      );
    }
    return [...yearlySubscriptions].sort(
      (a, b) => a.subscription_number - b.subscription_number,
    );
  }, [monthlySubscriptions, yearlySubscriptions]);
  const yearlyByNumber = useMemo(() => {
    const map = new Map();
    yearlySubscriptions.forEach((plan) => {
      map.set(plan.subscription_number, plan);
    });
    return map;
  }, [yearlySubscriptions]);
  const scheduledDowngradeSubscription = useMemo(() => {
    if (!downgradeAtPeriodEnd || !getSubscriptionPackages) return null;
    return getSubscriptionPackages.find(
      (pkg) => Number(pkg.subscription_number) === Number(downgradeAtPeriodEnd),
    );
  }, [downgradeAtPeriodEnd, getSubscriptionPackages]);
  const comparisonRows = useMemo(
    () => buildComparisonRows(comparisonPlans),
    [comparisonPlans],
  );
  useEffect(() => {
    const restaurantId = params.restaurant_id || restaurants?.id;
    if (!restaurantId) return;
    dispatch(
      syncSubscriptionFromStripeAction({
        restaurant_id: restaurantId,
      }),
    ).catch((error) => {
      console.warn("[page load] subscription sync failed:", error);
    });
  }, [dispatch, params.restaurant_id, restaurants?.id]);
  useEffect(() => {
    const status = restaurants?.subscription_status || "";
    setActiveOrTrial(["active", "trialing"].includes(status));
    setActiveSubNumber(normalizeSubLevel(restaurants?.sub_lvl));
    setDowngradeAtPeriodEnd(
      normalizeSubLevel(restaurants?.downgrade_at_period_end),
    );
    setCadence(restaurants?.yearly_or_monthly ?? null);
    setSubEndIso(restaurants?.subscription_end_date ?? null);
  }, [
    restaurants?.subscription_status,
    restaurants?.sub_lvl,
    restaurants?.downgrade_at_period_end,
    restaurants?.yearly_or_monthly,
    restaurants?.subscription_end_date,
  ]);
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const checkout = query.get("checkout");
    const sessionId = query.get("session_id");
    const portalUpdate = query.get("portal_update");
    if (checkoutHandledRef.current) return;
    const isCheckoutSuccess = checkout === "success" && sessionId;
    const isPortalUpdateSuccess = portalUpdate === "success";
    if (!isCheckoutSuccess && !isPortalUpdateSuccess) return;
    checkoutHandledRef.current = true;
    const finalizeHostedStripeReturn = async () => {
      setIsFinalizingCheckout(true);
      setCheckoutSuccess(false);
      setHostedReturnType(isPortalUpdateSuccess ? "portal_update" : "checkout");
      try {
        if (isCheckoutSuccess) {
          const result = await dispatch(
            confirmSubscriptionCheckoutSessionAction({
              restaurant_id: params.restaurant_id,
              session_id: sessionId,
            }),
          );
          console.log("[checkout return] confirmed:", result);
        }
        if (isPortalUpdateSuccess) {
          const result = await dispatch(
            syncSubscriptionFromStripeAction({
              restaurant_id: params.restaurant_id,
            }),
          );
          console.log("[portal update return] synced from Stripe:", result);
        }
        await dispatch(
          fetchSubscriptionStatus({
            restaurant_id: params.restaurant_id,
          }),
        );
        await dispatch(fetchRestaurantDetails(params.restaurant_id));
        setCheckoutSuccess(true);
        _setIsConfirmationModal(true);
        navigate(location.pathname, {
          replace: true,
        });
      } catch (error) {
        console.error("[hosted Stripe return] confirmation failed:", error);
        alert(
          error?.message ||
            t("ui.i18nFinalCleanup.stripeCompletedSubscriptionSyncFailed"),
        );
        navigate(location.pathname, {
          replace: true,
        });
      } finally {
        setIsFinalizingCheckout(false);
      }
    };
    finalizeHostedStripeReturn();
  }, [
    dispatch,
    location.search,
    location.pathname,
    navigate,
    params.restaurant_id,
  ]);
  const onConfirmation = () => {
    if (consumeRestaurantSetupTourPending(params.restaurant_id)) {
      startRestaurantSetupTour(params.restaurant_id);
      navigate(`/place_design/${params.restaurant_id}`);
      return;
    }
    navigate(`/reservationfromrestaurant/${params.restaurant_id}/null/all`);
  };
  useEffect(() => {
    if (step === "choose_subscription") {
      dispatch(resetCreatePaymentIntent());
    }
  }, [step, dispatch]);
  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
    dispatch(resetConfirmSubscription());
    dispatch(fetchRestaurantDetails(params.restaurant_id));
  }, [dispatch, params.restaurant_id]);
  useEffect(() => {
    if (!getSubscriptionPackages) return;
    const packages = [...getSubscriptionPackages].sort(
      (a, b) => a.subscription_number - b.subscription_number,
    );
    const monthly = packages.filter(
      (p) => Number(p.number_of_days) > 0 && Number(p.number_of_days) < 90,
    );
    const yearly = packages.filter((p) => Number(p.number_of_days) >= 300);
    setMonthlySubscriptions(monthly);
    setYearlySubscriptions(yearly);
  }, [getSubscriptionPackages]);
  useEffect(() => {
    if (cadence === "monthly") {
      setShowMonthly(true);
    } else if (cadence === "yearly") {
      setShowMonthly(false);
    }
  }, [cadence]);
  const handleSelectSubscription = (subscription) => {
    const selectedNumber = normalizeSubLevel(subscription?.subscription_number);
    const targetSubscription = shouldUseTrialPlanInstead({
      selectedNumber,
      hasStarterPack3Months,
      hasPremiumMonthly: Boolean(premiumMonthlySubscription),
      activeOrTrial,
    })
      ? premiumMonthlySubscription
      : subscription;
    if (!targetSubscription) return;
    const targetNumber = normalizeSubLevel(
      targetSubscription.subscription_number,
    );
    const isCurrent = activeOrTrial && targetNumber === Number(activeSubNumber);
    const isAlreadyScheduledDowngrade =
      activeOrTrial &&
      downgradeAtPeriodEnd > 0 &&
      targetNumber === Number(downgradeAtPeriodEnd);

    // Current plan is only blocked when there is no scheduled downgrade.
    // If there is a scheduled downgrade, clicking current plan means:
    // cancel the scheduled downgrade and keep current plan.
    if (isCurrent && downgradeAtPeriodEnd === 0) return;
    if (isAlreadyScheduledDowngrade) return;
    if (
      !isCurrent &&
      targetNumber !== 1 &&
      !hasStarterPack3Months &&
      activeOrTrial &&
      restaurants?.id
    ) {
      dispatch(
        createUpgradeIntent({
          restaurantId: restaurants.id,
          body: {
            new_subscription_number: targetSubscription.subscription_number,
          },
        }),
      );
    }
    setSelectedSubscription(targetSubscription);
    setShowMoreDetails(false);
    setStep("payment");
  };
  const handleSetConfirmation = useCallback((val) => {
    _setIsConfirmationModal(val);
  }, []);
  const handleStarterPackSubscribe = async () => {
    setIsActivatingStarterPack(true);
    try {
      await dispatch(
        activateStarterPack({
          restaurantId: params.restaurant_id,
        }),
      );
      await dispatch(
        fetchSubscriptionStatus({
          restaurant_id: params.restaurant_id,
        }),
      );
      _setIsConfirmationModal(true);
    } catch (error) {
      console.error("Erro ao ativar starter pack:", error);
      alert(error.message || t("ui.i18nFinalCleanup.offerActivationFailed"));
    } finally {
      setIsActivatingStarterPack(false);
    }
  };
  const handleFreePlanSubscribe = async () => {
    if (!selectedSubscription || normalizeSubLevel(selectedSubscription.subscription_number) !== 1) {
      return;
    }

    setIsActivatingFreePlan(true);
    try {
      await dispatch(
        activateFreePlan({
          restaurantId: params.restaurant_id,
          packageId: selectedSubscription.id,
          numberOfDays: selectedSubscription.number_of_days,
        }),
      );
      await dispatch(
        fetchSubscriptionStatus({
          restaurant_id: params.restaurant_id,
        }),
      );
      await dispatch(fetchRestaurantDetails(params.restaurant_id));
      setCheckoutSuccess(true);
      setHostedReturnType("free_plan");
      _setIsConfirmationModal(true);
    } catch (error) {
      console.error("Erro ao ativar plano gratuito:", error);
      alert(error.message || t("ui.screens.choosePlanAndPay.freePlanActivationFailed"));
    } finally {
      setIsActivatingFreePlan(false);
    }
  };

  const handleCancelScheduledDowngrade = async () => {
    const restaurantId = params.restaurant_id || restaurants?.id;
    if (!restaurantId) return;
    setIsCancellingScheduledDowngrade(true);
    try {
      await dispatch(
        cancelScheduledDowngradeAction({
          restaurant_id: restaurantId,
        }),
      );
      await dispatch(
        fetchSubscriptionStatus({
          restaurant_id: restaurantId,
        }),
      );
      await dispatch(fetchRestaurantDetails(restaurantId));
      setCheckoutSuccess(true);
      setHostedReturnType("cancel_scheduled_downgrade");
      _setIsConfirmationModal(true);
    } catch (error) {
      console.error("[cancel scheduled downgrade] failed:", error);
      alert(
        error?.message || t("ui.i18nFinalCleanup.cancelScheduledChangeFailed"),
      );
    } finally {
      setIsCancellingScheduledDowngrade(false);
    }
  };
  const handleScrollToComparison = () => {
    comparisonRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return isNaN(d) ? "" : d.toLocaleDateString(appLocale);
    } catch {
      return "";
    }
  };
  const isFreePlanFlow =
    selectedSubscription &&
    normalizeSubLevel(selectedSubscription.subscription_number) === 1;
  const isStarterPackFlow =
    !isFreePlanFlow &&
    hasStarterPack3Months &&
    premiumMonthlySubscription &&
    selectedSubscription &&
    Number(selectedSubscription.subscription_number) ===
      Number(premiumMonthlySubscription.subscription_number);
  const isSelectedCurrentToCancelDowngrade =
    !isStarterPackFlow &&
    activeOrTrial &&
    downgradeAtPeriodEnd > 0 &&
    selectedSubscription &&
    normalizeSubLevel(selectedSubscription.subscription_number) ===
      Number(activeSubNumber);
  const isSelectedYearToMonth =
    !isStarterPackFlow &&
    cadence === "yearly" &&
    selectedSubscription?.number_of_days === 30;
  const isSelectedDowngrade =
    !isStarterPackFlow &&
    activeOrTrial &&
    selectedSubscription &&
    normalizeSubLevel(selectedSubscription.subscription_number) <
      Number(activeSubNumber);
  const displayAmount =
    activeOrTrial &&
    !isSelectedDowngrade &&
    !isSelectedYearToMonth &&
    !isSelectedCurrentToCancelDowngrade &&
    upgradePaymentIntent?.amount_cents != null
      ? (upgradePaymentIntent.amount_cents * 0.01).toFixed(2)
      : (selectedSubscription?.total_value ?? 0);
  const renderComparisonTable = (plans, rows) => {
    if (!plans.length) return null;
    return (
      <ComparisonBlock>
        <ComparisonBlockTitle>
          {t("ui.screens.choosePlanAndPay.comparacaoDePlanos")}
        </ComparisonBlockTitle>

        <ComparisonTableWrap>
          <ComparisonTable>
            <thead>
              <tr>
                <th>{t("ui.screens.choosePlanAndPay.funcionalidade")}</th>
                {plans.map((plan) => (
                  <th key={`comparison-${plan.subscription_number}`}>
                    {displayPlanName(plan, t)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <TableFeatureTitle>
                    {t("ui.screens.choosePlanAndPay.precoMensal")}
                  </TableFeatureTitle>
                  <TableFeatureDesc>
                    {t("ui.screens.choosePlanAndPay.cobrancaMensal")}
                  </TableFeatureDesc>
                </td>

                {plans.map((plan) => (
                  <td key={`monthly-price-${plan.subscription_number}`}>
                    <PriceCell>
                      {formatMoney(plan.monthly_value, " / mês")}
                    </PriceCell>
                  </td>
                ))}
              </tr>

              <tr>
                <td>
                  <TableFeatureTitle>
                    {t("ui.screens.choosePlanAndPay.precoAnual")}
                  </TableFeatureTitle>
                  <TableFeatureDesc>
                    {t(
                      "ui.screens.choosePlanAndPay.cobrancaAnualComEquivalenteMensal",
                    )}
                  </TableFeatureDesc>
                </td>

                {plans.map((plan) => {
                  const yearlyPlan = yearlyByNumber.get(
                    plan.subscription_number,
                  );
                  return (
                    <td key={`yearly-price-${plan.subscription_number}`}>
                      {yearlyPlan ? (
                        <>
                          <PriceCell>
                            {formatMoney(yearlyPlan.total_value, " / ano")}
                          </PriceCell>
                          <SecondaryPrice>
                            {formatMoney(yearlyPlan.monthly_value, " / mês")}
                          </SecondaryPrice>
                        </>
                      ) : (
                        <NoMark>—</NoMark>
                      )}
                    </td>
                  );
                })}
              </tr>

              {rows.map((feature) => (
                <tr key={`feature-${feature.key}`}>
                  <td>
                    <TableFeatureTitle>{feature.title}</TableFeatureTitle>

                    {feature.desc ? (
                      <TableFeatureDesc>{feature.desc}</TableFeatureDesc>
                    ) : null}
                  </td>

                  {plans.map((plan, planIndex) => {
                    const hasFeature = planIndex >= feature.introducedAtIndex;
                    return (
                      <td
                        key={`feature-${feature.key}-${plan.subscription_number}`}
                      >
                        {hasFeature ? <YesMark>✔</YesMark> : <NoMark>—</NoMark>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </ComparisonTable>
        </ComparisonTableWrap>
      </ComparisonBlock>
    );
  };
  const renderPacks = (subscription) => {
    const color = getPlanColor(subscription.subscription_number);
    const normalizedSubscriptionNumber = normalizeSubLevel(
      subscription.subscription_number,
    );
    const isCurrent =
      activeOrTrial && normalizedSubscriptionNumber === Number(activeSubNumber);
    const isAlreadyScheduledDowngrade =
      activeOrTrial &&
      downgradeAtPeriodEnd > 0 &&
      normalizedSubscriptionNumber === Number(downgradeAtPeriodEnd);
    const isCurrentClickableToCancelDowngrade =
      isCurrent && downgradeAtPeriodEnd > 0;
    const isDisabled =
      isAlreadyScheduledDowngrade ||
      (isCurrent && !isCurrentClickableToCancelDowngrade);
    const allOffers = splitOffersSafely(subscription.offers || "");
    const shortOffers = allOffers.slice(0, FEATURE_PREVIEW_COUNT);
    const isZeroPrice = Number(subscription?.monthly_value ?? 0) === 0;
    return (
      <Col
        sm={12}
        md={4}
        key={subscription.subscription_number}
        className="mb-4 d-flex"
      >
        <PlanCard
          $selected={isCurrent}
          $disabledPlan={isAlreadyScheduledDowngrade}
        >
          {isCurrent && (
            <ActiveBadge>
              {t("ui.screens.choosePlanAndPay.planoAtual")}
            </ActiveBadge>
          )}

          {isAlreadyScheduledDowngrade && (
            <ScheduledBadge>
              {t("ui.screens.choosePlanAndPay.alteracaoJaAgendada")}
            </ScheduledBadge>
          )}

          <PlanName color={color}>{displayPlanName(subscription, t)}</PlanName>

          <PlanDescription>{subscription.description}</PlanDescription>

          <PriceLine>
            <PlanPrice color={color}>
              {isZeroPrice
                ? t("ui.i18nCleanup2.gratis")
                : `${subscription.monthly_value} €`}
            </PlanPrice>

            <PerMonth>
              {isZeroPrice ? null : t("loginScreen.plans.perMonth")}
            </PerMonth>
          </PriceLine>

          {subscription.number_of_days === 365 && (
            <DiscountNote>
              {t("ui.screens.choosePlanAndPay.inclui20DeDescontoEmRelacao")}
            </DiscountNote>
          )}

          <SubscribeButton
            color={color}
            onClick={() => handleSelectSubscription(subscription)}
            disabled={isDisabled}
          >
            {isCurrent
              ? isCurrentClickableToCancelDowngrade
                ? t("ui.screens.choosePlanAndPay.manterPlanoAtual")
                : t("ui.screens.choosePlanAndPay.planoAtual")
              : isAlreadyScheduledDowngrade
                ? t("ui.i18nCleanup2.jaAgendado")
                : hasStarterPack3Months
                  ? t("ui.i18nCleanup2.experimenteGratis")
                  : t("ui.screens.choosePlanAndPay.subscrever")}
          </SubscribeButton>

          {isCurrentClickableToCancelDowngrade && (
            <WarningInline>
              {t(
                "ui.screens.choosePlanAndPay.cliqueParaCancelarAAlteracaoAgendada",
              )}
            </WarningInline>
          )}

          {isAlreadyScheduledDowngrade && (
            <WarningInline>
              {t("ui.screens.choosePlanAndPay.estePlanoJaEstaAgendadoPara")}
            </WarningInline>
          )}

          {shortOffers.length > 0 && (
            <FeatureList as="ul" iconColor={color}>
              {shortOffers.map((raw, idx) => {
                const { title, desc } = splitTitleAndDesc(raw);
                return (
                  <OfferItem key={idx} iconColor={color}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </span>

                    <div className="text">
                      <div className="title">{title}</div>

                      {desc && <div className="desc">{desc}</div>}
                    </div>
                  </OfferItem>
                );
              })}
            </FeatureList>
          )}

          <SeeMorePlansButton onClick={handleScrollToComparison}>
            {t("ui.screens.choosePlanAndPay.verMais")}
          </SeeMorePlansButton>
        </PlanCard>
      </Col>
    );
  };
  return (
    <Container>
      {isFinalizingCheckout && <LoaderComponent activate={true} />}

      {step === "choose_subscription" ? (
        <>
          <Row>
            {showHeader && (
              <CardHeader>
                <CardHeaderTitle>
                  {t("ui.screens.choosePlanAndPay.plano")}
                </CardHeaderTitle>
              </CardHeader>
            )}

            {activeOrTrial && restaurants?.id && !hasStarterPack3Months && (
              <Col xs={12}>
                <BackButton
                  onClick={() =>
                    navigate(`/subscription_management/${restaurants.id}`)
                  }
                >
                  <FontAwesomeIcon icon={faCircleLeft} />
                  {t("ui.screens.choosePlanAndPay.gerirPlano")}
                </BackButton>
              </Col>
            )}

            <Col xs={12} className="d-flex justify-content-center mb-4 gap-2">
              <ButtonPlans
                isSelected={showMonthly}
                onClick={() => setShowMonthly(true)}
              >
                {t("ui.screens.choosePlanAndPay.mensal")}
              </ButtonPlans>

              <ButtonPlans
                isSelected={!showMonthly}
                onClick={() => setShowMonthly(false)}
              >
                {t("ui.screens.choosePlanAndPay.anual")}
              </ButtonPlans>
            </Col>

            {activeOrTrial &&
              downgradeAtPeriodEnd > 0 &&
              scheduledDowngradeSubscription && (
                <Col xs={12} className="mb-3">
                  <ScheduledDowngradeNotice>
                    {t(
                      "ui.screens.choosePlanAndPay.existeUmaAlteracaoDePlanoAgendada",
                    )}{" "}
                    <b>{displayPlanName(scheduledDowngradeSubscription, t)}</b>
                    {t("ui.screens.choosePlanAndPay.noFimDoPeriodoAtualEsse")}
                  </ScheduledDowngradeNotice>
                </Col>
              )}

            {hasStarterPack3Months && premiumMonthlySubscription && (
              <Col xs={12} className="mb-4 d-flex justify-content-center">
                <StarterPackButton
                  onClick={() =>
                    handleSelectSubscription(premiumMonthlySubscription)
                  }
                >
                  {t(
                    "ui.screens.choosePlanAndPay.experimenteGratisOPlanoCompletoDurante",
                  )}
                  <StarterPackSubtext>
                    {t(
                      "ui.screens.choosePlanAndPay.ofertaAtivaAoClicarEmQualquer",
                    )}
                  </StarterPackSubtext>
                </StarterPackButton>
              </Col>
            )}

            <Col xs={12}>
              <Row>{visibleSubscriptions.map((sub) => renderPacks(sub))}</Row>
            </Col>
          </Row>

          <ComparisonSection ref={comparisonRef}>
            <ComparisonHeader>
              <ComparisonTitle>
                {t("ui.screens.choosePlanAndPay.comparacaoDePlanos2")}
              </ComparisonTitle>

              <ComparisonSubtitle>
                {t("ui.screens.choosePlanAndPay.vejaPrecosMensaisAnuaisETodas")}
              </ComparisonSubtitle>
            </ComparisonHeader>

            {renderComparisonTable(comparisonPlans, comparisonRows)}
          </ComparisonSection>
        </>
      ) : (
        <>
          {showHeader && (
            <CardHeader>
              <CardHeaderTitle>
                {t("ui.screens.choosePlanAndPay.plano2")}
              </CardHeaderTitle>
            </CardHeader>
          )}

          <Row className="mb-2">
            <Col xs={12}>
              <BackButton onClick={() => setStep("choose_subscription")}>
                <FontAwesomeIcon icon={faCircleLeft} />
                {t("ui.screens.choosePlanAndPay.voltar")}
              </BackButton>
            </Col>
          </Row>

          <Row className="align-items-start">
            <Col xs={12} md={6} className="mb-4">
              {selectedSubscription && (
                <PaymentPlanCard>
                  {(() => {
                    const color = getPlanColor(
                      selectedSubscription.subscription_number,
                    );
                    const endDate = subEndIso ? fmtDate(subEndIso) : null;
                    const fullOffers = splitOffersSafely(
                      selectedSubscription.offers || "",
                    );
                    const previewOffers = FEATURE_PREVIEW_COUNT
                      ? fullOffers.slice(0, FEATURE_PREVIEW_COUNT)
                      : fullOffers;
                    const canToggle =
                      FEATURE_PREVIEW_COUNT &&
                      fullOffers.length > FEATURE_PREVIEW_COUNT;
                    return (
                      <>
                        <PaymentTitle>
                          {isFreePlanFlow
                            ? t("ui.screens.choosePlanAndPay.freePlanTitle")
                            : isStarterPackFlow
                              ? t("ui.i18nCleanup2.ativarOferta")
                              : isSelectedCurrentToCancelDowngrade
                                ? t(
                                    "ui.screens.choosePlanAndPay.manterPlanoAtual",
                                  )
                                : t("ui.screens.restaurantCreation.pagamento")}
                        </PaymentTitle>

                        <PlanName color={color}>
                          {displayPlanName(selectedSubscription, t)}
                        </PlanName>

                        <PlanDescription>
                          {isStarterPackFlow
                            ? t(
                                "ui.i18nCleanup2.planoCompletoComAcessoTotalAsFuncionalidades",
                              )
                            : isSelectedCurrentToCancelDowngrade
                              ? t(
                                  "ui.i18nCleanup2.aoManterOPlanoAtualAAlteracaoAgendadaSeraCancelada",
                                )
                              : t(
                                  "ui.i18nFinalCleanup.essentialPlanDescription",
                                  {
                                    cadence:
                                      selectedSubscription.number_of_days ===
                                      365
                                        ? t("ui.i18nFinalCleanup.yearly")
                                        : t("ui.i18nFinalCleanup.monthly"),
                                  },
                                )}
                        </PlanDescription>

                        {isStarterPackFlow ? (
                          <DiscountNote>
                            {t(
                              "ui.screens.choosePlanAndPay.text3MesesGratisSemQualquerCompromisso",
                            )}
                          </DiscountNote>
                        ) : hasStarterPack3Months && !isFreePlanFlow ? (
                          <DiscountNote>
                            {t(
                              "ui.screens.choosePlanAndPay.ofertaAtivaCompletoTudoIncluido3",
                            )}
                          </DiscountNote>
                        ) : null}

                        <PriceLine>
                          <PlanPrice color={color}>
                            {isStarterPackFlow || isFreePlanFlow
                              ? t("ui.i18nCleanup2.gratis")
                              : `${displayAmount} €`}
                          </PlanPrice>

                          {!isStarterPackFlow &&
                            !isFreePlanFlow &&
                            selectedSubscription.number_of_days === 365 && (
                              <PerMonth>
                                {Number(
                                  selectedSubscription.monthly_value ?? 0,
                                ) === 0
                                  ? t("ui.i18nCleanup2.gratis")
                                  : selectedSubscription.monthly_value}
                                {t("ui.i18nCleanup2.mes")}
                              </PerMonth>
                            )}
                        </PriceLine>

                        {isSelectedCurrentToCancelDowngrade && (
                          <WarningInline>
                            {t(
                              "ui.screens.choosePlanAndPay.aoConfirmarAAlteracaoDePlano",
                            )}
                          </WarningInline>
                        )}

                        {(isSelectedYearToMonth || isSelectedDowngrade) &&
                          !isSelectedCurrentToCancelDowngrade && (
                            <WarningInline>
                              {isFreePlanFlow ? (
                                <>
                                  {t(
                                    "ui.screens.choosePlanAndPay.freePlanDowngradeText",
                                  )}
                                </>
                              ) : isSelectedYearToMonth ? (
                                <>
                                  {t(
                                    "ui.screens.choosePlanAndPay.estaAAlterarDe",
                                  )}
                                  <b>
                                    {t("ui.screens.choosePlanAndPay.anual2")}
                                  </b>
                                  {t("ui.screens.choosePlanAndPay.para")}{" "}
                                  <b>
                                    {t("ui.screens.choosePlanAndPay.mensal2")}
                                  </b>
                                  {t(
                                    "ui.screens.choosePlanAndPay.oNovoPrecoMensalSeraAplicado",
                                  )}{" "}
                                  {endDate ? (
                                    <>
                                      {t(
                                        "ui.screens.choosePlanAndPay.noFimDoPeriodoAtual",
                                      )}
                                      <b>{endDate}</b>
                                      ).
                                    </>
                                  ) : (
                                    <>
                                      {t(
                                        "ui.screens.choosePlanAndPay.noFimDoPeriodoAtual2",
                                      )}
                                    </>
                                  )}{" "}
                                  {t(
                                    "ui.screens.choosePlanAndPay.naoSeraCobradoAgoraMasPode",
                                  )}
                                </>
                              ) : (
                                <>
                                  {t(
                                    "ui.screens.choosePlanAndPay.estaAReduzirOPlanoA",
                                  )}{" "}
                                  {endDate && (
                                    <>
                                      (<b>{endDate}</b>)
                                    </>
                                  )}
                                  {t(
                                    "ui.screens.choosePlanAndPay.naoSeraCobradoAgoraMasPode2",
                                  )}
                                </>
                              )}
                            </WarningInline>
                          )}

                        {!isStarterPackFlow &&
                          !isFreePlanFlow &&
                          selectedSubscription.number_of_days === 365 && (
                            <DiscountNote>
                              {t(
                                "ui.screens.choosePlanAndPay.inclui20DeDescontoEmRelacao2",
                              )}
                            </DiscountNote>
                          )}

                        {!showMoreDetails ? (
                          <>
                            <FeatureList as="ul" iconColor={color}>
                              {previewOffers.map((raw, idx) => {
                                const { title, desc } = splitTitleAndDesc(raw);
                                return (
                                  <OfferItem key={idx} iconColor={color}>
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faCircleCheck} />
                                    </span>

                                    <div className="text">
                                      <div className="title">{title}</div>

                                      {desc && (
                                        <div className="desc">{desc}</div>
                                      )}
                                    </div>
                                  </OfferItem>
                                );
                              })}
                            </FeatureList>

                            {canToggle && (
                              <ViewMore
                                color={color}
                                onClick={() => setShowMoreDetails(true)}
                              >
                                {t("ui.screens.choosePlanAndPay.verMais2")}
                              </ViewMore>
                            )}
                          </>
                        ) : (
                          <>
                            <FeatureList as="ul" iconColor={color}>
                              {fullOffers.map((raw, idx) => {
                                const { title, desc } = splitTitleAndDesc(raw);
                                return (
                                  <OfferItem key={idx} iconColor={color}>
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faCircleCheck} />
                                    </span>

                                    <div className="text">
                                      <div className="title">{title}</div>

                                      {desc && (
                                        <div className="desc">{desc}</div>
                                      )}
                                    </div>
                                  </OfferItem>
                                );
                              })}
                            </FeatureList>

                            {canToggle && (
                              <ViewMore
                                color={color}
                                onClick={() => setShowMoreDetails(false)}
                              >
                                {t("ui.screens.choosePlanAndPay.verMenos")}
                              </ViewMore>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </PaymentPlanCard>
              )}
            </Col>

            <Col xs={12} md={6} className="mb-4">
              {selectedSubscription &&
                (isFreePlanFlow ? (
                  <StarterPackInfoBox>
                    <StarterPackInfoTitle>
                      {t("ui.screens.choosePlanAndPay.freePlanTitle")}
                    </StarterPackInfoTitle>

                    <StarterPackInfoText>
                      {activeOrTrial && Number(activeSubNumber) > 1
                        ? t("ui.screens.choosePlanAndPay.freePlanDowngradeText")
                        : t("ui.screens.choosePlanAndPay.freePlanNoCardText")}
                    </StarterPackInfoText>

                    <StarterPackSubscribeButton
                      onClick={handleFreePlanSubscribe}
                      disabled={isActivatingFreePlan}
                    >
                      {isActivatingFreePlan
                        ? t("ui.i18nCleanup2.aAtivar")
                        : t("ui.screens.choosePlanAndPay.activateFreePlan")}
                    </StarterPackSubscribeButton>
                  </StarterPackInfoBox>
                ) : isStarterPackFlow ? (
                  <StarterPackInfoBox>
                    <StarterPackInfoTitle>
                      {t(
                        "ui.screens.choosePlanAndPay.ativeOSeuPeriodoGratuito",
                      )}
                    </StarterPackInfoTitle>

                    <StarterPackInfoText>
                      {t("ui.screens.choosePlanAndPay.aoClicar")}{" "}
                      <b>{t("ui.screens.choosePlanAndPay.subscrever")}</b>{" "}
                      {t("ui.screens.choosePlanAndPay.vaiAtivarOPlano")}{" "}
                      <b>{t("ui.screens.choosePlanAndPay.completo")}</b>{" "}
                      {t("ui.screens.choosePlanAndPay.durante")}{" "}
                      <b>{t("ui.screens.choosePlanAndPay.text3Meses")}</b>{" "}
                      {t("ui.screens.choosePlanAndPay.semQualquerCompromisso")}
                    </StarterPackInfoText>

                    <StarterPackSubscribeButton
                      onClick={handleStarterPackSubscribe}
                      disabled={isActivatingStarterPack}
                    >
                      {isActivatingStarterPack
                        ? t("ui.i18nCleanup2.aAtivar")
                        : t("ui.screens.choosePlanAndPay.subscrever")}
                    </StarterPackSubscribeButton>
                  </StarterPackInfoBox>
                ) : isSelectedCurrentToCancelDowngrade ? (
                  <StarterPackInfoBox>
                    <StarterPackInfoTitle>
                      {t("ui.screens.choosePlanAndPay.manterPlanoAtual")}
                    </StarterPackInfoTitle>

                    <StarterPackInfoText>
                      {t(
                        "ui.screens.choosePlanAndPay.aoConfirmarAAlteracaoDePlano2",
                      )}
                    </StarterPackInfoText>

                    <StarterPackSubscribeButton
                      onClick={handleCancelScheduledDowngrade}
                      disabled={isCancellingScheduledDowngrade}
                    >
                      {isCancellingScheduledDowngrade
                        ? t("ui.i18nCleanup2.aCancelarAlteracao")
                        : t("ui.i18nCleanup2.cancelarAlteracaoAgendada")}
                    </StarterPackSubscribeButton>
                  </StarterPackInfoBox>
                ) : (
                  <>
                    <StripeCreatePayMethod
                      subscription_number={
                        selectedSubscription.subscription_number
                      }
                      restaurant_id={params.restaurant_id}
                      handleSetConfirmation={handleSetConfirmation}
                      isUpdate={activeOrTrial}
                    />

                    <p
                      style={{
                        textAlign: "center",
                        color: "#555",
                        fontSize: "12px",
                        marginTop: "12px",
                      }}
                    >
                      {t(
                        "ui.screens.choosePlanAndPay.todosOsPlanosSaoRenovadosAutomaticamente",
                      )}
                      <br />
                      {t(
                        "ui.screens.choosePlanAndPay.podeCancelarAQualquerMomento",
                      )}
                    </p>
                  </>
                ))}
            </Col>
          </Row>
        </>
      )}

      <ConfirmationModal
        show={_isConfirmationModal}
        onHide={() => _setIsConfirmationModal(false)}
        onConfirm={onConfirmation}
        onCancel={() => _setIsConfirmationModal(false)}
        title={
          isStarterPackFlow
            ? t("ui.i18nCleanup2.planoAtivado")
            : checkoutSuccess &&
                hostedReturnType === "cancel_scheduled_downgrade"
              ? t("ui.i18nCleanup2.alteracaoCancelada")
              : checkoutSuccess && hostedReturnType === "portal_update"
                ? t("ui.i18nCleanup2.alteracaoConcluida")
                : checkoutSuccess
                  ? t("ui.i18nCleanup2.pagamentoConcluido")
                  : t("ui.i18nCleanup2.dadosDePagamentoRegistados")
        }
        body={
          isStarterPackFlow
            ? t(
                "ui.i18nCleanup2.oPlanoCompletoFoiAtivadoComSucessoDurante3MesesGratisJaPodeComecarAGer",
              )
            : checkoutSuccess &&
                hostedReturnType === "cancel_scheduled_downgrade"
              ? t(
                  "ui.i18nCleanup2.aAlteracaoAgendadaFoiCanceladaComSucessoOPlanoAtualSeraMantido",
                )
              : checkoutSuccess && hostedReturnType === "portal_update"
                ? t(
                    "ui.i18nCleanup2.aAlteracaoDoPlanoFoiConcluidaComSucessoJaPodeComecarAGerirAsSuasReserv",
                  )
                : checkoutSuccess
                  ? t(
                      "ui.i18nCleanup2.oPagamentoFoiConcluidoComSucessoJaPodeComecarAGerirAsSuasReservas",
                    )
                  : t(
                      "ui.i18nCleanup2.osDadosDePagamentoForamRegistadosComSucessoJaPodesComecarAGerirAsTuasR",
                    )
        }
        confirmLabel={t("ui.i18nCleanup.startManaging")}
        confirmColor="#ee754c"
        centered={true}
      />
    </Container>
  );
}
export default ChoosePlanAndPay;
